import React from 'react';
import { Container, Heading } from '@charlietango/ui';
import { rem } from '../../styles/rem';

import { useAuth } from '../../api/AuthProvider';
import Accordion from '../../components/Accordion/Accordion';
import RichText from '../../components/RichText/RichText';
import { colors } from '../../styles/colors';
import { TextVariant } from '../../styles/typography';
import AccordionViewModel from '../../view-models/AccordionViewModel';
import TextBlockViewModel from '../../view-models/TextBlockViewModel';

export type HelpPageProps = {
  intro?: TextBlockViewModel;
  faq?: AccordionViewModel;
  richText?: TextBlockViewModel;
};

function HelpPage({ intro, faq, richText }: HelpPageProps) {
  const { loginUrl, logoutUrl } = useAuth();

  return (
    <>
      {intro && (
        <Container
          sx={{
            maxWidth: 'unset',
            mx: 0,
            px: [rem(20), rem(20), 0],
            py: [rem(80), rem(96), rem(120)],
            borderBottom: `1px solid rgba(31, 111, 111, 0.20)`,
          }}
        >
          <div sx={{ maxWidth: rem(952), mx: 'auto' }}>
            {intro.title && (
              <Heading
                as="h1"
                variant={TextVariant.Header3xl}
                sx={{
                  mb: intro.text ? [rem(16), rem(16), rem(20)] : 0,
                  color: colors.base.Text.Primary,
                }}
              >
                {intro.title}
              </Heading>
            )}
            {intro.text && (
              <RichText
                variant={TextVariant.BodyLg}
                params={{ loginUrl, logoutUrl }}
                sx={{
                  maxWidth: rem(800),
                  mb: 0,
                  color: colors.base.Text.Primary,
                  '> p': {
                    my: 0,
                  },
                }}
              >
                {intro.text}
              </RichText>
            )}
          </div>
        </Container>
      )}

      {faq && (
        <Container
          sx={{
            maxWidth: 'unset',
            mx: 0,
            px: [rem(20), rem(20), 0],
            py: [rem(64), rem(96), rem(120)],
          }}
        >
          <div sx={{ maxWidth: rem(952), mx: 'auto' }}>
            {faq.title && (
              <Heading
                as="h2"
                variant={TextVariant.HeaderXl}
                sx={{ mb: [rem(32), rem(48), rem(64)] }}
              >
                {faq.title}
              </Heading>
            )}
            <div sx={{ mx: 0 }}>
              <Accordion
                items={faq.items}
                interpolationParams={{ loginUrl, logoutUrl }}
              />
            </div>
          </div>
        </Container>
      )}

      {richText?.text && (
        <Container
          sx={{
            maxWidth: 'unset',
            mx: 0,
            px: [rem(20), rem(20), 0],
            pb: [rem(80), rem(96), rem(120)],
          }}
        >
          <div sx={{ maxWidth: rem(952), mx: 'auto' }}>
            <RichText
              variant={TextVariant.BodyLg}
              params={{ loginUrl, logoutUrl }}
              sx={{
                color: colors.base.Text.Primary,
                maxWidth: rem(800),
                '> h2, > h3': {
                  color: colors.base.Text.Primary,
                },
                '> p': {
                  mt: 0,
                  mb: rem(36),
                },
              }}
            >
              {richText.text}
            </RichText>
          </div>
        </Container>
      )}
    </>
  );
}

HelpPage.displayName = 'HelpPage';

export default HelpPage;
